/* so-drawer-swipe.js — swipe down to close the theme's own cart drawer and quick-add drawer on phones
   (Task 26, 2026-09-28). Loaded once site-wide (deferred, after so-sheet-swipe.js) from snippets/so-postcode.liquid.
   No vendor file is touched: this bolts window.soSheetSwipe (assets/so-sheet-swipe.js) onto the two persistent
   drawer elements (<cart-drawer>, <quick-add-drawer>, both SideDrawer: open/close by the `open` attribute).

   - A 24px-tall grab strip (the touch target; the quick-add's only handle on phones) holding the .so-sheet__handle pill from so-primitives.css) is added as the drawer's first child, phones
     only. The drawers replace their inner HTML (cart-drawer.js renderContents / refreshFromPage after an add),
     so it is put back whenever it goes missing.
   - Drag ONLY from the grab bar or the drawer header (handleOnly) — never from the item list, which people
     scroll and tap +/− in, nor from the checkout footer.
   - Closing goes through the drawer's own close() (focus return, overlay, listeners). ×, overlay tap and
     Escape are unchanged. Desktop (side drawers) is unaffected: the helper only acts at (max-width: 767.98px),
     the same query as the grab bar's CSS, and the theme turns drawers into bottom sheets below 768px. */
(function () {
  'use strict';
  var MEDIA = '(max-width: 767.98px)';
  var KINDS = {
    'CART-DRAWER': { handle: '.so-drawer-grab, .drawer__header' },
    // The quick-add header is hidden on phones (md:flex); its sticky mobile × sits in the content, so the
    // grab bar is the handle there (plus the header wherever it shows).
    'QUICK-ADD-DRAWER': { handle: '.so-drawer-grab, .quick-add__header' }
  };

  // The soSheetSwipe.attach options for one drawer element (pure enough to test in node with a stub element).
  function drawerSwipeOptions(el, doc) {
    var k = KINDS[String(el && el.tagName).toUpperCase()];
    if (!k) return null;
    return {
      handle: k.handle,
      handleOnly: true,
      scroller: '.drawer__content',
      backdrop: function () { return doc ? doc.querySelector('.js-overlay.is-visible') : null; },
      onClose: function () { if (typeof el.close === 'function') el.close(); },
      isOpen: function () { return el.hasAttribute('open'); },
      media: MEDIA
    };
  }

  // Task 30: after the cart re-renders (a line removed while the list was scrolled down) the scroller can keep a
  // scrollTop past its new end — iOS shows the top cut off, empty space below and a list that seems frozen.
  // Returns the scrollTop to set, or null to leave it: 0 when the content now fits, the new end when past it.
  function clampScrollTop(scrollTop, scrollHeight, clientHeight) {
    var max = Math.max(0, (scrollHeight || 0) - (clientHeight || 0));
    if (max <= 1) return scrollTop > 0 ? 0 : null;
    return scrollTop > max ? max : null;
  }

  var pure = { drawerSwipeOptions: drawerSwipeOptions, clampScrollTop: clampScrollTop, MEDIA: MEDIA, KINDS: KINDS };
  if (typeof module === 'object' && module.exports) module.exports = pure;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.__soDrawerSwipe) return;
  window.__soDrawerSwipe = true;

  var CSS =
    '.so-drawer-grab{display:none}' +
    '@media ' + MEDIA + '{' +
      '.so-drawer-grab{display:block;position:sticky;top:0;z-index:5;flex:0 0 auto;height:24px;' +
        'background:rgba(var(--drawer-bg-color));border-radius:inherit;touch-action:none;cursor:grab}' +
      // the .so-sheet__handle pill, block-level, in the delivery sheet's grab colour (the primitive's --so-line
      // is almost the drawer's own beige)
      '.so-drawer-grab .so-sheet__handle{display:block;width:40px;height:4px;margin:10px auto 0;border-radius:2px;background:#c9bda6}' +   // centred in the 24px touch strip
      'cart-drawer > .so-drawer-grab ~ .drawer__header{top:24px}' +     // the sticky header sits under the bar
      'cart-drawer .drawer__header,quick-add-drawer .quick-add__header{touch-action:none}' +
    '}';

  function style() {
    if (document.getElementById('so-drawer-swipe-css')) return;
    var st = document.createElement('style');
    st.id = 'so-drawer-swipe-css';
    st.textContent = CSS;
    document.head.appendChild(st);
  }
  function ensureGrab(el) {
    var first = el.firstElementChild;
    if (first && first.classList.contains('so-drawer-grab')) return;
    var old = el.querySelector(':scope > .so-drawer-grab');
    if (old) { el.insertBefore(old, el.firstChild); return; }
    var g = document.createElement('div');
    g.className = 'so-drawer-grab';
    g.setAttribute('aria-hidden', 'true');
    g.innerHTML = '<span class="so-sheet__handle"></span>';
    el.insertBefore(g, el.firstChild);
  }
  // Once per drawer element: the element persists (only its inner HTML is re-rendered).
  function setup(el) {
    if (!el || el.__soDrawerSwipe || !window.soSheetSwipe) return;
    var opts = drawerSwipeOptions(el, document);
    if (!opts) return;
    el.__soDrawerSwipe = true;
    style();
    ensureGrab(el);
    window.soSheetSwipe.attach(el, opts);
    new MutationObserver(function () { ensureGrab(el); }).observe(el, { childList: true });
    // Re-renders (cart-items.js swaps the lines, renderContents the whole drawer): clamp the scrollers once the
    // DOM settles. Runs on every width — harmless where nothing is out of range.
    var t = 0;
    new MutationObserver(function () {
      if (t) return;
      t = setTimeout(function () { t = 0; clampAll(el); }, 60);
    }).observe(el, { childList: true, subtree: true });
  }
  function clampAll(el) {
    var list = [el].concat(Array.prototype.slice.call(el.querySelectorAll('.drawer__content, .quick-add__content')));
    list.forEach(function (s) {
      var v = clampScrollTop(s.scrollTop, s.scrollHeight, s.clientHeight);
      if (v !== null) s.scrollTop = v;
    });
  }
  function scan() { document.querySelectorAll('cart-drawer, quick-add-drawer').forEach(setup); }

  // Attach when a drawer opens (and once at boot for the ones already on the page).
  new MutationObserver(function (ms) {
    for (var i = 0; i < ms.length; i++) {
      var t = ms[i].target;
      if (t.hasAttribute && t.hasAttribute('open') && KINDS[t.tagName]) setup(t);
    }
  }).observe(document.documentElement, { attributes: true, attributeFilter: ['open'], subtree: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan); else scan();
})();
